import {
  TypographyType,
  SingleSelectionType,
  TextareaType,
  TypographyFromDataType,
  ImageType,
  SelfCheckType,
  TypographyFromDashboardType,
  PercentageType,
  PieChartType,
  ElementType,
  ContentType,
  celltype,
  AudioType,
  ThoughtmapType,
  PresetType,
  ReferenceType,
  AiCommentType,
  ConceptItemType,
} from "data/CellComponent";
import TextareaInput from "components/task/userInput/TextareaInput";
import SelectInput from "components/task/userInput/SelectInput";
import TypographyFromDataCell from "components/task/content/block/TypographyFromDataCell";
import * as _ from "lodash";
import ButtonGroupInput from "components/task/userInput/ButtonGroupInput";
import { Box } from "@mui/joy";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import RadioInput from "../../userInput/RadioInput";
import {
  getTaskIdFromTaskKey,
  getPercentageSum,
  getReferenceBlock,
  getAutomaticThoughtId,
} from "logic/logics";
import { ProgramContentType } from "data/BlockComponent";
import React, { memo } from "react";
import TypographyFromDashboardCell from "./TypographyFromDashboardCell";
import PercentageInput from "components/task/userInput/PercentageInput";
import PieChartCell from "./PieChartCell";
import DateSelect from "components/task/userInput/DateSelect";
import AiText from "components/ai/AiText";
import ExtractFromThoughtRecord from "components/task/userInput/ExtractFromThoughtRecord";
import TypographyFromConceptNoteCell from "./TypographyFromConceptNoteCell";
import SidebarOpenButton from "components/common/SidebarOpenButton";
import TypographyCell from "./TypographyCell";
import RequestPushNotificationButton from "components/common/RequestPushNotificationButton";
import LoginButtonByDevice from "components/user/LoginButtonByDevice";
import { useTranslation } from "react-i18next";
import AudioButton from "components/task/userInput/AudioButton";
import DateHeaderCell from "./DateHeaderCell";
import ThoughtmapCell from "./ThoughtmapCell";
import RequestStoreReviewButton from "components/common/RequestStoreReviewButton";
import useGetLocale from "hooks/useGetLocale";
import GotoPaymentButton from "components/common/GoToPaymentButton";
import PresetInput from "components/task/content/cell/PresetInput";
import ReferenceInput from "../cell/ReferenceInput";
import SelectIntermediateBelief from "components/task/userInput/SelectIntermediateBelief";
import AiCommentInput from "../cell/AiCommentInput";
import ConceptNoteCell from "./ConceptItemCell";
import Feedback from "components/task/userInput/Feedback";
import { automaticThoughtIdType } from "api/conceptApi";
import { taskSectionAtom } from "recoil/taskSectionAtom";
import { currentTaskSectionIndexAtom } from "recoil/currentTaskSectionIndexAtom";

function Cell({
  type,
  contentStr,
  blockIndex,
  lineIndex,
  cellIndex,
  taskKey,
  data,
  setData,
  translationVersion,
  readonly,
}: {
  type: celltype;
  contentStr: string;
  blockIndex: number;
  lineIndex: number;
  cellIndex: number;
  taskKey: string;
  data?: ProgramContentType[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  translationVersion?: string;
  readonly: boolean;
}) {
  const content: ContentType = JSON.parse(contentStr);
  const translationKey_raw = (content as TypographyType).translationKey;
  const translationKey =
    translationKey_raw?.substring(translationKey_raw.length - 1) === "."
      ? translationKey_raw.substring(0, translationKey_raw.length - 1)
      : translationKey_raw;
  const user = useRecoilValue(userAtom);
  const isCoach = (user?.accessToken && user?.role !== "patient") || false;

  const taskSectionState = useRecoilValue(taskSectionAtom);
  const currentTaskSectionIndex = useRecoilValue(currentTaskSectionIndexAtom);
  const { openTaskKeyList, currentTaskKey } = (taskSectionState || [])[
    currentTaskSectionIndex
  ];

  const locale = useGetLocale();

  const { t } = useTranslation("translation", {
    keyPrefix: `task.activity.${getTaskIdFromTaskKey(
      taskKey
    )}.${translationVersion}`,
  });

  const { t: t_thoughtRecord } = useTranslation("translation", {
    keyPrefix: `task.activity.2-2-T.v1`,
  });

  const { t: t_common } = useTranslation("translation", {
    keyPrefix: "task.activity.common",
  });

  const translatedText = !translationVersion
    ? translationKey || ""
    : translationKey?.startsWith("common")
    ? t_common(translationKey?.split(":")[1] || "")
    : translationKey === "$A$"
    ? t_common(translationKey || "")
    : (content as TypographyType)?.isLabel
    ? t_common(translationKey || "")
    : translationKey?.includes("2-2-T:")
    ? t_thoughtRecord(translationKey?.split(":")[1] || "")
    : t(translationKey || "");

  const translatedOptions =
    (content as SingleSelectionType)["options"]
      ?.filter((element) => element.text || t(element.translationKey || ""))
      .map((each) => each.text || t(each.translationKey || "")) || [];

  const translationKeys =
    (content as SingleSelectionType)["options"]
      ?.filter((element) => t(element.translationKey || ""))
      .map((each) => each.translationKey || "") || [];
  const translationKeys_additional =
    (content as SingleSelectionType)["options"]
      ?.filter((element) => t(element.translationKey_additional || ""))
      .map((each) => each.translationKey_additional || "") || [];

  return type === "typography" ? (
    <TypographyCell
      {...content}
      text={
        (content as TypographyType)?.text || (content as TypographyType)?.value
          ? (content as TypographyType)?.text ||
            (content as TypographyType)?.value
          : (content as TypographyType)?.translationKey
          ? translatedText
          : ""
      }
    />
  ) : type === "dateHeader" ? (
    <DateHeaderCell {...content} />
  ) : type === "image" ? (
    <div className="w-full">
      <img
        alt={"empty"}
        src={
          (content as ImageType).imageUrl.includes(".png")
            ? taskKey.startsWith("4-5")
              ? require(`../../../../static/activity/${`4-5-A`}/${
                  (content as ImageType).imageUrl
                }`)
              : taskKey.includes("_")
              ? require(`../../../../static/activity/${taskKey.split("_")[0]}/${
                  (content as ImageType).imageUrl
                }`)
              : require(`../../../../static/activity/${getTaskIdFromTaskKey(
                  `${taskKey}`
                )}/${(content as ImageType).imageUrl}`)
            : taskKey.startsWith("4-5")
            ? require(`../../../../static/activity/${`4-5-A`}/${
                (content as ImageType).imageUrl
              }_${locale}.png`)
            : taskKey.includes("_")
            ? require(`../../../../static/activity/${taskKey.split("_")[0]}/${
                (content as ImageType).imageUrl
              }_${locale}.png`)
            : require(`../../../../static/activity/${getTaskIdFromTaskKey(
                `${taskKey}`
              )}/${(content as ImageType).imageUrl}_${locale}.png`)
        }
      />
    </div>
  ) : type === "select" ? (
    <SelectInput
      defaultValue={
        (content as SingleSelectionType).selectedTranslationKey
          ? t((content as SingleSelectionType).selectedTranslationKey || "")
          : (content as SingleSelectionType).value
      }
      selectedIndex={(content as SingleSelectionType).selectedIndex}
      index={blockIndex}
      options={translatedOptions}
      {...(translationKeys.every((element) => element.length > 0) && {
        optionKeys: translationKeys,
      })}
      {...(translationKeys_additional.every(
        (element) => element.length > 0
      ) && { optionKeys_additional: translationKeys_additional })}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      isCoachField={(content as ElementType).coach}
      isCoach={isCoach}
    />
  ) : type === "buttongroup" ? (
    <ButtonGroupInput
      defaultValue={
        (content as SingleSelectionType).selectedTranslationKey
          ? t((content as SingleSelectionType).selectedTranslationKey || "")
          : (content as SingleSelectionType).value
      }
      index={blockIndex}
      options={translatedOptions}
      {...(translationKeys.every((element) => element.length > 0) && {
        optionKeys: translationKeys,
      })}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      isCoach={isCoach}
    />
  ) : type === "textarea" ? (
    <TextareaInput
      taskKey={taskKey || ""}
      readOnly={(content as TextareaType).coach && !isCoach}
      placeholder={(content as TextareaType).placeholder}
      initialHtmlString={(content as TextareaType).htmlString}
      initialValue={(content as TextareaType).value}
      isCoachField={(content as TextareaType).coach}
      isCoach={isCoach}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
      editorKey={(content as TextareaType)?.editorKey}
      id={(content as TextareaType)?.id}
    />
  ) : type === "preset" ? (
    <PresetInput
      taskKey={taskKey || ""}
      readOnly={(content as TextareaType).coach && !isCoach}
      placeholder={(content as TextareaType).placeholder}
      initialHtmlString={(content as TextareaType).htmlString}
      initialValue={(content as TextareaType).value || undefined}
      initialSelectedIndex={(content as SingleSelectionType).selectedIndex}
      isCoachField={(content as TextareaType).coach}
      isCoach={isCoach}
      setData={setData}
      options={translatedOptions}
      {...(translationKeys.every((element) => element.length > 0) && {
        optionKeys: translationKeys,
      })}
      {...(translationKeys_additional.every(
        (element) => element.length > 0
      ) && { optionKeys_additional: translationKeys_additional })}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
      editorKey={(content as TextareaType)?.editorKey}
      presetKey={(content as PresetType)?.presetKey}
    />
  ) : type === "reference" ? (
    <ReferenceInput
      taskKey={taskKey || ""}
      placeholder={(content as TextareaType).placeholder}
      initialHtmlString={(content as TextareaType).htmlString}
      initialValue={(content as TextareaType).value}
      isCoach={isCoach}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
      editorKey={(content as TextareaType)?.editorKey}
      referenceKey={(content as ReferenceType)?.referenceKey}
    />
  ) : type === "aiComment" ? (
    <AiCommentInput
      taskKey={taskKey || ""}
      initialValue={(content as AiCommentType).value}
      aiCommentValue={(content as AiCommentType).aiCommentValue}
      isCoach={isCoach}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
      editorKey={(content as AiCommentType)?.editorKey}
      aiCommentKey={(content as AiCommentType)?.aiCommentKey}
    />
  ) : type === "aiCoaching" ? (
    <AiText
      text={content?.value || ""}
      onClick={() => {
        setData((data) => {
          if (data) {
            const data_temp = _.cloneDeep(data);
            const currentContent =
              data_temp[blockIndex].lines[lineIndex - 1][0].content || {};

            data_temp[blockIndex].lines[lineIndex - 1][0].content = {
              ...currentContent,
              value: content?.value,
            };

            return data_temp;
          }

          return data;
        });
      }}
    />
  ) : type === "typographyFromDashboard" ? (
    <TypographyFromDashboardCell
      taskKey={taskKey}
      initialValue={content.value}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
      id1={(content as TypographyFromDashboardType).id1}
      id2={(content as TypographyFromDashboardType).id2}
      level={(content as TypographyFromDashboardType).level}
    />
  ) : type === "typographyFromConceptNote" ? (
    <TypographyFromConceptNoteCell
      taskKey={taskKey || ""}
      initialHtmlString={(content as TextareaType).htmlString}
      initialValue={(content as TextareaType).value}
      isCoach={isCoach}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
      editorKey={(content as TextareaType)?.editorKey}
      id1={(content as TypographyFromDashboardType).id1}
      id2={(content as TypographyFromDashboardType).id2}
    />
  ) : type === "conceptItem" ? (
    <ConceptNoteCell
      taskKey={taskKey || ""}
      conceptKey={(content as ConceptItemType).conceptKey}
    />
  ) : type === "selfCheck" ? (
    <RadioInput
      defaultValue={(content as SelfCheckType).selectedScore}
      index={blockIndex}
      evaluation={Object.fromEntries(
        Object.entries((content as SelfCheckType).evaluation).map(
          ([key, value]) => [t_common(key), value]
        )
      )}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
    />
  ) : type === "extractFromThoughtRecord" ? (
    <ExtractFromThoughtRecord
      automaticThoughtId={
        getAutomaticThoughtId(
          getTaskIdFromTaskKey(taskKey)
        ) as automaticThoughtIdType
      }
      defaultValue={content.value}
      index={blockIndex}
      data={data || []}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
    />
  ) : type === "login" ? (
    <LoginButtonByDevice />
  ) : type === "payment" ? (
    <GotoPaymentButton disabled={readonly} />
  ) : type === "pushNotification" ? (
    <RequestPushNotificationButton disabled={readonly} />
  ) : type === "storeReview" ? (
    <RequestStoreReviewButton disabled={readonly} />
  ) : type === "sidebarOpenButton" ? (
    <SidebarOpenButton disabled={readonly} />
  ) : type === "audio" ? (
    <AudioButton
      isCurrentTask={taskKey === currentTaskKey}
      url={require(`../../../../static/audio/${
        (content as AudioType).audioUrl
      }_${locale}.mp3`)}
      initialTime={Math.floor((content as AudioType).currentTime || 0)}
      setData={setData}
      index={blockIndex}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      isCoach={isCoach}
      disabled={readonly}
    />
  ) : type === "divider" ? (
    <Box
      sx={{
        height: "1px",
        width: "100%",
        backgroundColor: "background.level2",
      }}
    />
  ) : type === "date" ? (
    <DateSelect
      editorKey={(content as ElementType)?.editorKey}
      defaultValue={(content as ElementType).value}
      index={blockIndex}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
    />
  ) : type === "percentage" ? (
    <PercentageInput
      id={(content as PercentageType).id}
      initialPercentage={
        content?.complementPercentageIdList
          ? Math.max(
              0,
              100 -
                getPercentageSum(
                  data || [],
                  content?.complementPercentageIdList
                )
            )
          : (content as PercentageType).percentage
      }
      inputKey={`${(content as PercentageType).editorKey}`}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
    />
  ) : type === "typographyFromData" ? (
    <TypographyFromDataCell
      taskKey={taskKey}
      data={data || []}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
      dataMapping={(content as TypographyFromDataType).dataMapping}
      initialValue={(content as TypographyFromDataType).value}
      translationVersion={translationVersion}
      text={
        (content as TypographyType)?.translationKey
          ? translatedText
          : (content as TypographyType)?.text || ""
      }
    />
  ) : type === "pieChart" ? (
    <PieChartCell
      data={data || []}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
      pieChartId={(content as PieChartType).pieChartId}
    />
  ) : type === "thoughtmap" ? (
    <ThoughtmapCell
      from={(content as ThoughtmapType).from}
      to={(content as ThoughtmapType).to}
      fixedValue={(content as ThoughtmapType).fixedValue}
    />
  ) : type === "selectIntermediateBelief" ? (
    <SelectIntermediateBelief
      selectedIndex={(content as SingleSelectionType).selectedIndex}
      index={blockIndex}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
    />
  ) : type === "feedback" ? (
    <Feedback
      taskKey={taskKey || ""}
      savedFeedback={(content as TextareaType).value}
      setData={setData}
      lineIndex={lineIndex}
      cellIndex={cellIndex}
      index={blockIndex}
      editorKey={(content as TextareaType)?.editorKey}
    />
  ) : (
    <div></div>
  );
}

export default memo(Cell);
